/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: a week-by-week program. Week 1…N, each an ordered list of
 * workouts: members see a tab per week and the workouts in this order.
 */
import React, { useEffect, useState } from "react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@lemonadetraining/web-ui";
import { IconCheck, IconPlus } from "@tabler/icons-react";
import { updateProgrammingWeeks } from "@lemonadetraining/web-shared";
import { useProgramWorkouts } from "../../../hooks/useProgramWorkouts";
import { WorkoutSlot } from "./WorkoutSlot";
import { useWorkoutNotes } from "../../../hooks/useWorkoutNotes";
import { usePlannerEditing, type Placement } from "./usePlannerEditing";
import { CopyWeekDialog } from "./CopyWeekDialog";
import type { PlannerProps } from "./plannerProps";

export const MAX_WEEKS = 12;

export function WeekPlanner({
  program,
  canManage,
  canChangeWeeks = false,
  request,
  onRequestHandled,
  onWeeksChange,
  noteLabel,
}: PlannerProps) {
  const data = useProgramWorkouts(program.programmingUID, { kind: "all" });
  const notes = useWorkoutNotes(
    program.programmingUID,
    data.workouts.map((w) => w.id),
    canManage && Boolean(noteLabel)
  );
  const notesFor = noteLabel
    ? {
        label: noteLabel,
        byId: notes.notes,
        failed: notes.status === "error",
        onRetry: notes.reload,
      }
    : undefined;
  const ready = data.status === "ready";
  const highestUsed = data.workouts.reduce((max, w) => Math.max(max, w.workoutData.week ?? 0), 0);
  const [weeks, setWeeks] = useState(() => Math.max(1, program.weeks ?? 1));
  const shownWeeks = Math.min(MAX_WEEKS, Math.max(weeks, highestUsed));
  const [week, setWeek] = useState(1);
  const [weeksBusy, setWeeksBusy] = useState(false);
  const [weeksError, setWeeksError] = useState<string | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const inWeek = data.workouts.filter((w) => w.workoutData.week === week);

  const editing = usePlannerEditing({
    programName: program.name,
    workouts: data,
    notes:
      canManage && noteLabel
        ? { label: noteLabel, status: notes.status, byId: notes.notes, save: notes.save }
        : undefined,
    describe: (placement: Placement) => `Week ${placement.week ?? week}`,
    renderPlacementControl: (placement, onChange) => (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Move to</span>
        <Select
          value={String(placement.week ?? week)}
          onValueChange={(v) => onChange({ ...placement, week: Number(v) })}
        >
          <SelectTrigger className="w-auto" aria-label="Move to week" data-testid="move-to-week">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: shownWeeks }, (_, i) => i + 1).map((n) => (
              <SelectItem key={n} value={String(n)}>
                Week {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ),
  });

  useEffect(() => {
    if (!request) return;
    if (request === "add") editing.openCreate({ week });
    if (request === "copy") setCopyOpen(true);
    onRequestHandled();
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeWeeks = async (next: number) => {
    setWeeksBusy(true);
    setWeeksError(null);
    try {
      await updateProgrammingWeeks(program.programmingUID, next);
      setWeeks(next);
      onWeeksChange?.(next);
      if (week > next) setWeek(next);
    } catch (error: unknown) {
      console.error("Error changing the number of weeks:", error);
      setWeeksError("We couldn't change the number of weeks. Check your connection and try again.");
    } finally {
      setWeeksBusy(false);
    }
  };

  // Only once the plan has loaded: an empty list while loading is not an empty week.
  const lastWeekEmpty =
    ready && shownWeeks > 1 && !data.workouts.some((w) => w.workoutData.week === shownWeeks);
  const count = inWeek.length;

  return (
    <div className="flex flex-col gap-4" data-testid="week-planner">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Weeks">
          {Array.from({ length: shownWeeks }, (_, i) => i + 1).map((n) => {
            const planned = data.workouts.filter((w) => w.workoutData.week === n).length;
            return (
              <Button
                key={n}
                variant={n === week ? "selected" : "outline"}
                aria-pressed={n === week}
                onClick={() => setWeek(n)}
                data-testid={`week-${n}`}
                aria-label={`Week ${n}, ${
                  planned === 0
                    ? "nothing planned"
                    : `${planned} ${planned === 1 ? "workout" : "workouts"}`
                }`}
              >
                Week {n}
                {planned > 0 && <IconCheck className="size-3.5" aria-hidden="true" />}
              </Button>
            );
          })}
        </div>
        {canManage && canChangeWeeks && ready && shownWeeks < MAX_WEEKS && (
          <Button
            variant="ghost"
            onClick={() => changeWeeks(shownWeeks + 1)}
            disabled={weeksBusy}
            data-testid="add-week"
          >
            <IconPlus className="size-4" aria-hidden="true" />
            Add a week
          </Button>
        )}
        {canManage && canChangeWeeks && lastWeekEmpty && (
          <Button
            variant="ghost"
            onClick={() => changeWeeks(shownWeeks - 1)}
            disabled={weeksBusy}
            data-testid="remove-week"
          >
            Remove week {shownWeeks}
          </Button>
        )}
      </div>
      {weeksError && (
        <p role="alert" className="text-sm text-destructive">
          {weeksError}
        </p>
      )}

      <WorkoutSlot
        heading={`Week ${week} of ${shownWeeks}`}
        subheading={
          ready
            ? count === 0
              ? "Nothing planned yet"
              : `${count} ${count === 1 ? "workout" : "workouts"}`
            : undefined
        }
        workouts={inWeek}
        status={data.status}
        canManage={canManage}
        emptyText={
          canManage ? "Nothing planned for this week yet." : "Nothing planned for this week."
        }
        onAdd={() => editing.openCreate({ week })}
        onEdit={editing.openEdit}
        onDuplicate={editing.duplicate}
        onRemove={editing.requestRemove}
        onRetry={data.reload}
        notes={canManage ? notesFor : undefined}
      />
      <p className="text-xs text-muted-foreground">
        Members see a tab for each week, with that week&apos;s workouts in this order.
      </p>
      {editing.nodes}
      <CopyWeekDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        program={program}
        source={{ kind: "week", week, weeks: shownWeeks, workouts: inWeek }}
        status={data.status}
        create={data.create}
        onCopied={data.reload}
      />
    </div>
  );
}
